import React, { useEffect, useState } from "react";
import { ClientRestaurant } from '../../types';
import MembershipProgress from './components/MembershipProgress/MembershipProgress';
import AddRestaurantModal from './components/AddRestaurantModal/AddRestaurantModal';
import { FaPlus } from 'react-icons/fa';
import './Home.scss';

interface HomeProps {
    restaurants: ClientRestaurant[];
    onRestaurantClick: (restaurant: ClientRestaurant) => void;
}

const Home: React.FC<HomeProps> = ({ restaurants, onRestaurantClick }) => {
    const [totalSavings, setTotalSavings] = useState(0);
    const [showAddRestaurantModal, setShowAddRestaurantModal] = useState(false);
    const [clientName, setClientName] = useState<string>('Cliente');

    useEffect(() => {
        // Get client name from localStorage
        const clientData = localStorage.getItem('client');
        if (clientData) {
            const client = JSON.parse(clientData);
            const firstName = client?.username?.split(' ')[0] || client?.email?.split('@')[0] || 'Cliente';
            setClientName(firstName);
        }
    }, []);

    useEffect(() => {
        // Calculate total savings from all restaurants
        console.log("RESTAURANTS:", restaurants);
        const total = restaurants.reduce((sum, restaurant) => sum + restaurant.total_saved, 0);
        console.log("TOTAL SAVINGS:", total);
        setTotalSavings(total);
    }, [restaurants]);

    const formatChileanPesos = (amount: number): string => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const handleSubscribe = (code: string) => {
        console.log('Subscribing to restaurant with code:', code);
        // TODO: Implement backend API call to subscribe to restaurant
        alert(`Suscripción exitosa con código: ${code}`);
    };

    // Sort restaurants by visits (most frequented)
    const sortedByVisits = [...restaurants].sort((a, b) => b.visits - a.visits);

    return (
        <div className="home-container">
            {/* Total Savings Card with Glassmorphism */}
            <div className="savings-card">
                <div className="savings-content">
                    <h2 className="savings-greeting">Hola, {clientName} 👋</h2>
                    <p className="savings-label">TOTAL AHORRADO</p>
                    <h2 className="savings-amount">{formatChileanPesos(totalSavings)}</h2>
                    <p className="savings-description">En todos tus restaurantes favoritos</p>
                </div>
            </div>

            {/* Restaurants Section */}
            <section className="restaurants-section">
                <div className="section-header">
                    <h2 className="section-title">Tus Favoritos</h2>
                    <button 
                        className="add-restaurant-btn"
                        onClick={() => setShowAddRestaurantModal(true)}
                        title="Agregar restaurante"
                    >
                        <FaPlus />
                    </button>
                </div>
                <div className="restaurants-list">
                    {sortedByVisits.map((restaurant) => (
                        <div 
                            key={restaurant.local_id} 
                            className="restaurant-card"
                            onClick={() => onRestaurantClick(restaurant)}
                        >
                            <div className="restaurant-header">
                                <div className="restaurant-info">
                                    <h3 className="restaurant-name-link">
                                        {restaurant.local_name}
                                    </h3>
                                    <span className="membership-badge">{restaurant.membership_name}</span>
                                </div>
                                <div className="header-right">
                                    <div className="discount-badge">
                                        -{restaurant.membership_discount}%
                                    </div>
                                    <p className="last-visit-corner">
                                        {new Date(restaurant.last_visit).toLocaleDateString('es-ES')}
                                    </p>
                                </div>
                            </div>

                            <div className="restaurant-content">
                                <div className="restaurant-stats">
                                    <div className="stat">
                                        <span className="stat-label">Visitas</span>
                                        <span className="stat-value">{restaurant.visits}</span>
                                    </div>
                                    <div className="stat stat-highlight">
                                        <span className="stat-label">Ahorrado</span>
                                        <span className="stat-value">{formatChileanPesos(restaurant.total_saved)}</span>
                                    </div>
                                </div>

                                {restaurant.next_level_name && restaurant.next_level_required_spent && (
                                    <MembershipProgress
                                        currentLevel={restaurant.membership_name}
                                        nextLevelName={restaurant.next_level_name}
                                        totalSpent={restaurant.total_spent}
                                        nextLevelRequiredSpent={restaurant.next_level_required_spent}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Add Restaurant Modal */}
            <AddRestaurantModal
                show={showAddRestaurantModal}
                onClose={() => setShowAddRestaurantModal(false)}
                onSubscribe={handleSubscribe}
            />
        </div>
    );
};

export default Home;